/* eslint-disable @tanstack/query/exhaustive-deps */
/**
 * All query keys should not require additional dependencies if those values do not change throughout the user journey (e.g. stateKey, applicationId).
 */
import { useEffect } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import {
  GET_EMAIL_VALIDATION_KEY,
  GetApplicationResponse,
  GetCompanySearchResponse,
  GetEmailAvailabilityResponse,
  GetEmailValidationResponse,
  GetOpenBankingActiveAccountsResponse,
  fetchGetApplication,
  fetchGetApplicationsOffers,
  fetchGetContext,
  fetchGetContextByStateKey,
  fetchGetDirectorSearch,
  fetchGetDocumentUpload,
  fetchGetFormalOffers,
  fetchGetOpenBankingActiveAccounts,
  fetchGetProfile,
  fetchGetSignupBranding,
  fetchGetSplitTest,
  fetchGetStateByStateKey,
  fetchGetUserConsent,
} from '@iwoca/lapi-client/edge';
import {
  API_AUTH_TOKEN,
  fetchLendingAPIJSON,
} from '@iwoca/lapi-client/lendingAPI';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { TUploadRejectReasonCode } from '../../components/TradingHistory/UploadButton/UploadButton';
import { getApplicantEmail } from '../../helpers/customerState';
import { getQueryParams } from '../../helpers/getQueryParams';
import { isLoggedInStep } from '../../helpers/isLoggedInStep';
import { useAuth } from '../../hooks/useAuth';
import { useContextStateKey } from '../../hooks/useContextStateKey';
import { useCurrentStep } from '../../hooks/useCurrentStep';
import { useLocale } from '../../hooks/useLocale';
import { useSignupParams } from '../../hooks/useSignupParams';
import { useImpersonatedStateKey } from '../../hooks/useStateKey';
import { TGetStateResponseDE } from '../../lapiOverrideTypesDE';
import { IwQueryClient } from '../../store/IwQueryClient';
import {
  TGetContext,
  TGetFormalOffersResponse,
  TGetOffersResponse,
  TGetStateResponse,
  TIsolatedSplitTests,
} from '../../types';
import {
  GET_BRANDING_ERRORED,
  GET_PROFILE_ERRORED,
} from '../../utils/queryCache';
import { trackApplicantEmail } from '../../utils/tracking';

export function useGetBrandingQuery() {
  const { affiliateTag, isPartnerCustomer } = useSignupParams();
  const impersonatedStateKey = useImpersonatedStateKey();
  // Direct customers never hit the affiliate_link_enabled branch below, so
  // skip the auth probes for them.
  const { isLoggedIn, isLoading: isAuthLoading } = useAuth({
    enabled: isPartnerCustomer,
  });

  const fetchGetSignupBrandingWithImpersonation = async () => {
    const query: { state_key?: string } = {};
    if (impersonatedStateKey) {
      query.state_key = impersonatedStateKey;
    }

    return await fetchGetSignupBranding({
      affiliateTag: affiliateTag as string,
      query,
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['fetchGetBranding', affiliateTag, impersonatedStateKey],
    queryFn: () => fetchGetSignupBrandingWithImpersonation(),
    enabled: isPartnerCustomer,
    meta: {
      errorMessage: GET_BRANDING_ERRORED,
    },
  });

  useEffect(() => {
    if (data && !isAuthLoading) {
      // Without affiliate_link_enabled a customer cannot create an account on step 0
      // These customers must login and proceed on later steps.
      if (!data.affiliate_link_enabled && !isLoggedIn) {
        window.location.href = '/login/';
      }
    }
  }, [data, isAuthLoading, isLoggedIn]);

  return {
    branding: data,
    isBrandingLoading: isLoading,
  };
}

export function useGetProfile() {
  const { rawAffiliateParam } = useSignupParams();
  const { isDE, isUK } = useLocale();
  const navigate = useNavigate();
  const currentStep = useCurrentStep();

  const handleError = () => {
    // Likelihood is that if they're trying to access a later step, they already have an account
    // But have somehow been logged out
    if (isLoggedInStep(currentStep)) {
      window.location.href = '/login/';
    } else if (!(
      (isUK && currentStep === 'new') ||
      (isDE && currentStep === 'neu')
    )) {
      const newStep = isDE ? 'neu' : 'new';

      // Persist params, possibly including company number for prefill
      navigate(`/${rawAffiliateParam}/${newStep}${window.location.search}`);
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['fetchGetProfile'],
    queryFn: () => fetchGetProfile({}),
    meta: {
      errorMessage: GET_PROFILE_ERRORED,
      handleGetProfileError: handleError,
    },
  });
  return {
    profile: data?.data,
    isProfileLoading: isLoading,
    isProfileErrored: isError,
  };
}

export function useGetContext() {
  const impersonatedStateKey = useImpersonatedStateKey();

  const { data, status, isLoading } = useQuery({
    queryKey: ['fetchGetContext'],
    queryFn: () =>
      impersonatedStateKey
        ? fetchGetContextByStateKey({ stateKey: impersonatedStateKey })
        : fetchGetContext({}),
  });

  return {
    context: data?.data as TGetContext['data'],
    contextStatus: status,
    isContextLoading: isLoading,
  };
}

export function useGetStateQuery<
  TStateResponse extends TGetStateResponse | TGetStateResponseDE,
>() {
  const { context } = useGetContext();
  const stateKey = context?.state_key;

  const { data, status, refetch, isLoading, isFetching } = useQuery({
    queryKey: ['fetchGetState'],
    queryFn: async () => {
      const data = await fetchGetStateByStateKey({ stateKey });

      const applicantEmail = getApplicantEmail(data);
      trackApplicantEmail(applicantEmail);

      return data;
    },
    enabled: Boolean(stateKey),
  });

  return {
    state: data?.data as TStateResponse['data'],
    refetchState: refetch,
    stateStatus: status,
    isStateLoading: isLoading,
    isStateFetching: isFetching,
  };
}

export function useGetSelectStateQuery<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  SelectFnType extends (data: any) => any,
>(selectFn: SelectFnType) {
  const { context } = useGetContext();
  const stateKey = context?.state_key;

  const { data } = useQuery({
    queryKey: ['fetchGetState'],
    queryFn: () => fetchGetStateByStateKey({ stateKey }),
    select: selectFn,
    enabled: Boolean(stateKey),
  });

  return {
    normalisedState: data as ReturnType<SelectFnType>,
  };
}

export function useGetApplicationQuery() {
  const { context } = useGetContext();
  const stateKey = context?.state_key;

  const { data, status, refetch, isLoading, isRefetching } =
    useQuery<GetApplicationResponse>({
      queryKey: ['fetchGetApplication'],
      queryFn: () => fetchGetApplication({ stateKey }),
      enabled: Boolean(stateKey),
    });

  return {
    application: data?.data as GetApplicationResponse['data'],
    refetchApplication: refetch,
    applicationStatus: status,
    isApplicationLoading: isLoading,
    isApplicationRefetching: isRefetching,
  };
}

export const useGetCompanyQuery = () => {
  const { company_number } = getQueryParams();

  const { data, isLoading } = useQuery({
    queryKey: ['getCompany', company_number],
    queryFn: () =>
      fetchIwocaGetCompanySearch({
        query: { company_name: company_number },
      }),
    enabled: Boolean(company_number),
  });

  return {
    company: data?.data.length === 1 ? data?.data[0] : null,
    isLoadingCompany: isLoading,
  };
};

export const useGetDirectorQuery = (companyNumber?: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ['getCompany', companyNumber],
    queryFn: () =>
      fetchGetDirectorSearch({ companyNumber: companyNumber as string }),
    enabled: Boolean(companyNumber),
  });

  return {
    directors: data?.data || null,
    isDirectorsLoading: isLoading,
  };
};

export const useSplitTestBranch = (
  experimentName: TIsolatedSplitTests,
  {
    enabled,
    cookieOverride = false,
  }: { enabled?: boolean; cookieOverride?: boolean },
) => {
  const cookieBranch = getCookie(`split_test_${experimentName}`);

  const shouldFetch =
    (!cookieOverride || (cookieOverride && !cookieBranch)) && enabled;

  const stateKey = useContextStateKey();

  const {
    refetch,
    data: branch,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['split_test', experimentName],
    queryFn: () =>
      fetchGetSplitTest({ stateKey: stateKey as string, experimentName }),
    select: (data) => data.data.branch,
    enabled: Boolean(stateKey) && shouldFetch,
  });

  return {
    isLoading,
    isError,
    refetchSplitTestBranch: refetch,
    splitTestBranch: branch || cookieBranch,
  };
};

export function useGetFormalOffers() {
  const stateKey = useContextStateKey();

  const { data, isLoading } = useQuery({
    queryKey: ['fetchGetFormalOffers'],
    queryFn: () => fetchGetFormalOffers({ stateKey: stateKey as string }),
    enabled: Boolean(stateKey),
  });

  return {
    formalOffersData: data?.data as
      TGetFormalOffersResponse['data'] | undefined,
    formalOffersLoading: isLoading,
  };
}

export const useGetOffersQuery = ({ enabled }: { enabled: boolean }) => {
  const { application } = useGetApplicationQuery();
  const applicationId = application?.application_id;

  const fetchGetApplicationsOffersWithApplicationInvalidation = async () => {
    const response = await fetchGetApplicationsOffers({
      applicationId: applicationId as string,
    });

    if (response) {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetApplication'],
      });
    }

    return response;
  };

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['offers'], // Do not expect application id to change during the user journey
    queryFn: () => fetchGetApplicationsOffersWithApplicationInvalidation(),
    enabled: enabled && Boolean(applicationId),
  });

  return {
    offersData: data?.data as TGetOffersResponse['data'] | undefined,
    isLoadingOffers: isLoading,
    refetchOffersData: refetch,
    isRefetchingOffers: isRefetching,
  };
};

export const useGetDocuments = (stateKey: string | undefined) => {
  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['documents'],
    queryFn: () => fetchGetDocumentUpload({ stateKey: stateKey as string }),
    enabled: Boolean(stateKey),
  });

  return {
    documents: (data as TGetDocumentsResponse) || undefined,
    isLoading,
    isRefetching,
    refetch,
  };
};

export async function fetchSaveUploadedDocuments({
  stateKey,
  body,
}: {
  stateKey: string;
  body: FormData;
}) {
  const response = await lendingApiFetchWithoutContentType(
    `/api/lending/edge/document_upload/${stateKey}/`,
    {
      method: 'POST',
      body,
    },
    { enctype: 'multipart/form-data' },
  );

  return await response.json();
}

export async function fetchGetEmailAvailability(email: string) {
  const response = await fetchLendingAPIJSON<GetEmailAvailabilityResponse>(
    '/api/lending/edge/email_availability/',
    {
      query: { email },
      overrideHeaders: {
        authorization: API_AUTH_TOKEN,
      },
    },
  );

  return response.data.available;
}

export async function fetchGetEmailValidation(email: string) {
  const response = await fetchLendingAPIJSON<GetEmailValidationResponse>(
    '/api/lending/edge/email_validation/',
    {
      query: { email },
      overrideHeaders: {
        authorization: API_AUTH_TOKEN,
      },
    },
  );

  return response.data;
}

export const getEmailValidationQueryOptions = (email: string) =>
  queryOptions({
    queryKey: [GET_EMAIL_VALIDATION_KEY, email],
    queryFn: async () => {
      try {
        return await fetchGetEmailValidation(email);
      } catch (error: unknown) {
        datadogLogs.logger.error(
          'Fetch email validation error',
          {},
          error as Error,
        );
        throw error;
      }
    },
  });

export const useGetEmailValidationQuery = (email?: string) => {
  const { data } = useQuery({
    ...getEmailValidationQueryOptions(email as string),
    enabled: Boolean(email),
  });

  return { emailValidation: data };
};

export async function fetchIwocaGetCompanySearch({
  query,
}: {
  query?: { company_name?: string };
}) {
  if (!query || !query.company_name) return;

  const companySearchParam = new URLSearchParams(query);

  const response = await fetchLendingAPIJSON<GetCompanySearchResponse>(
    `/api/companies/search/?${companySearchParam.toString()}`,
  );

  if (!response) throw new Error('No company search response');

  return response;
}

export const useGetUserConsent = (
  provider: string = 'truelayer_open_banking',
) => {
  const stateKey = useContextStateKey();

  const { data } = useQuery({
    queryKey: ['fetchGetUserConsent'],
    queryFn: () =>
      fetchGetUserConsent({
        stateKey: stateKey as string,
        provider,
      }),
    select: (data) => data.data,
    enabled: Boolean(stateKey),
  });

  return {
    userConsent: data?.consent_active ?? false,
  };
};

export const useGetOpenBankingActiveAccounts = ({
  stateKey,
  token,
}: {
  stateKey?: string;
  token?: string;
}) => {
  const { data, isLoading } = useQuery<GetOpenBankingActiveAccountsResponse>({
    queryKey: ['fetchGetOpenBankingActiveAccounts'],
    queryFn: () =>
      fetchGetOpenBankingActiveAccounts({
        stateKey: stateKey as string,
        token: token as string,
      }),
    enabled: Boolean(stateKey) && Boolean(token),
    staleTime: 0,
  });

  return {
    activeAccounts: data?.data,
    isLoading,
  };
};

export const getCookie = (name: string): string | null => {
  let cookieValue = null;
  if (typeof document !== 'undefined') {
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === name + '=') {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }
  return '';
};

export const lendingApiFetchWithoutContentType = (
  url: string,
  options = {},
  headers = {},
) =>
  fetch(url, {
    ...options,
    headers: {
      'x-csrftoken': getCookie('csrftoken') as string,
      ...headers,
    },
  });

type TDocumentStatus = 'complete' | 'rejected' | 'pending';

type TDocumentType =
  | 'company_accounts'
  | 'bank_statement'
  | 'business_accounts'
  | 'vat_return'
  | 'invoices'
  | 'personal_id'
  | 'customer_broker_consent'
  | 'balance_sheet'
  | 'profit_and_loss'
  | 'company_bylaws'
  | 'nif_card'
  | 'modelo'
  | 'management_accounts'
  | 'management_and_balance_accounts'
  | 'year_end_management_and_balance_accounts'
  | 'aged_debtor_report'
  | 'business_plan'
  | 'self_assessment_tax_return'
  | 'personal_bank_statement'
  | 'other'
  | 'cash_register'
  | 'cashflow_forecast'
  | 'outstanding_debt_information'
  | 'list_of_shareholders'
  | 'cbils_questionnaire'
  | 'year_end_management_accounts'
  | 'share_registry'
  | 'articles_of_association'
  | 'trade_certificate';

export type TFileStatusGroup = 'uploaded' | 'approved' | 'rejected';

export type TDocuments = {
  attributes: {
    document_name: string;
    document_type: string;
    end_date: Date;
    reject_reason: TUploadRejectReasonCode;
    start_date: Date;
    status: TDocumentStatus;
    uploaded_at: Date;
  };
  id: string;
  relationships: {
    bank_account: {
      data: {
        id: string;
        type: string;
      };
    };
  };
  type: TDocumentType;
  rejected?: number;
};

export type TGetDocumentsResponse =
  | {
      data: TDocuments[];
    }
  | undefined;
